// Club toast — Luma's small bottom-centre notice, drawn with the club design
// tokens so it follows tint + light/dark. The app-wide <Toaster/> (MUI-style
// white banner at the top) stays for the rest of Sotravel; every club surface
// (dashboard, club creation, public event page) mounts <ClubToaster/> and
// calls useClubToast() instead.
//
//   const toast = useClubToast();
//   toast.success("Saved");  toast.error("Could not save");  toast.info("…");
import React, { useSyncExternalStore } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { cn } from "./ui";

let items = [];
const listeners = new Set();
let counter = 0;
const emit = () => listeners.forEach((l) => l(items));
const subscribe = (l) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const snapshot = () => items;

const dismiss = (id) => {
  const next = items.filter((t) => t.id !== id);
  if (next.length !== items.length) {
    items = next;
    emit();
  }
};

const push = (message, type, options = {}) => {
  if (message == null || message === "") return null;
  const text = typeof message === "string" ? message : String(message);
  const existing = items.find((t) => t.message === text && t.type === type);
  if (existing) return existing.id;
  const id = ++counter;
  items = [...items.slice(-2), { id, message: text, type }];
  emit();
  const duration = options.duration ?? (type === "error" ? 6000 : 3500);
  if (Number.isFinite(duration) && duration > 0) setTimeout(() => dismiss(id), duration);
  return id;
};

export const clubToast = {
  success: (m, o) => push(m, "success", o),
  error: (m, o) => push(m, "error", o),
  info: (m, o) => push(m, "info", o),
  warning: (m, o) => push(m, "warning", o),
  dismiss,
};

export const useClubToast = () => clubToast;

const ICONS = {
  success: { Icon: CheckCircle2, className: "tw-text-[#2d8e21]" },
  error: { Icon: XCircle, className: "tw-text-[#d1352b]" },
  warning: { Icon: AlertTriangle, className: "tw-text-[#b98a27]" },
  info: { Icon: Info, className: "tw-text-muted-foreground" },
};

// Fixed bottom-centre stack. `className` lets a page outside .club-admin
// (public event page) pass the token scope + display.
export const ClubToaster = ({ className = "", ...attrs }) => {
  const list = useSyncExternalStore(subscribe, snapshot, snapshot);
  return (
    // The wrapper carries .club-admin only for the tokens; its page background
    // must not paint (it would draw a full-width strip behind the toast).
    <div className={cn("club-admin tw-pointer-events-none tw-fixed tw-inset-x-0 tw-bottom-[calc(1.5rem+var(--app-tabbar-inset,0px))] tw-z-[6000] tw-flex tw-flex-col tw-items-center tw-gap-2 tw-px-4", className)} style={{ background: "transparent" }} aria-live="polite" {...attrs}>
      <AnimatePresence initial={false}>
        {list.map((t) => {
          const { Icon, className: iconClass } = ICONS[t.type] || ICONS.info;
          return (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, y: 12, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.97 }}
              transition={{ duration: 0.18 }}
              className={cn("cs-popover-shadow tw-pointer-events-auto tw-flex tw-max-w-[520px] tw-items-center tw-gap-2.5 tw-bg-popover tw-py-2.5 tw-pl-3.5 tw-pr-2 tw-text-[14px] tw-font-medium tw-text-popover-foreground", "tw-rounded-[var(--r-md)]")}
              role={t.type === "error" ? "alert" : "status"}
            >
              <Icon className={cn("tw-size-4 tw-shrink-0", iconClass)} />
              <span className="tw-min-w-0 tw-flex-1">{t.message}</span>
              <button type="button" aria-label="Dismiss" onClick={() => dismiss(t.id)} className="tw-flex tw-size-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-muted-foreground tw-transition-colors hover:tw-bg-[var(--fill)] hover:tw-text-foreground">
                <X className="tw-size-3.5" />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default ClubToaster;
